import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';
import { masterEventService } from '../../services/masterEvent';
import { useNavigate } from 'react-router-dom';
import { AvatarDropdown } from '../Layout/MasterEventLayout';
import './MasterEventSelector.css';

const TYPE_META = {
  trip: { icon: '✈️', color: '#3b82f6' },
  gathering: { icon: '🎉', color: '#f59e0b' },
  league: { icon: '🏆', color: '#22c55e' },
  custom: { icon: '📋', color: '#a855f7' },
};

export default function MasterEventSelector() {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [eventMeta, setEventMeta] = useState({}); // { [eventId]: { members, activities, amOwner } }
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', type: 'gathering' });
  const [error, setError] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const refresh = useCallback(async () => {
    if (!user) return;
    const evts = await masterEventService.getForUser(user.id);
    setEvents(evts);
    const meta = {};
    await Promise.all(evts.map(async (event) => {
      const [members, activities] = await Promise.all([
        masterEventService.getMembers(event.id),
        masterEventService.getActivities(event.id),
      ]);
      const myMem = members.find((m) => m.userId === user?.id || m.user?.cognitoId === user?.cognitoId);
      meta[event.id] = {
        members,
        activities,
        amOwner: myMem?.role === 'owner' || event.createdBy === user?.id,
      };
    }));
    setEventMeta(meta);
  }, [user]);
  useEffect(() => { refresh(); }, [refresh]);

  const handleCreate = (e) => {
    e.preventDefault();
    setError('');
    if (!form.name.trim()) { setError('Event name is required'); return; }
    try {
      const newEvent = masterEventService.create({ name: form.name.trim(), description: form.description.trim(), type: form.type, createdBy: user.id });
      navigate(`/me/${newEvent.id}`);
    } catch (err) { setError(err.message); }
  };

  const handleDelete = (e, eventId, eventName) => {
    e.stopPropagation();
    if (!window.confirm(`Delete "${eventName}" and all its data? This cannot be undone.`)) return;
    masterEventService.delete(eventId);
    refresh();
  };

  const handleStartEdit = (e, eventId, currentName) => {
    e.stopPropagation();
    setEditingId(eventId);
    setEditName(currentName);
  };

  const handleSaveEdit = async (e, eventId) => {
    e.stopPropagation();
    if (!editName.trim()) return;
    setError('');
    try {
      await masterEventService.update(eventId, { name: editName.trim() });
      setEditingId(null);
      await refresh();
    } catch (err) {
      setError(err.message || 'Could not rename event');
    }
  };

  const handleCancelEdit = (e) => {
    e.stopPropagation();
    setEditingId(null);
  };

  const isOwnerOf = (event) => {
    return eventMeta[event.id]?.amOwner || event.createdBy === user?.id;
  };

  const isRootAdmin = user?.email?.toLowerCase() === 'chrispalm84@gmail.com' || user?.role === 'admin';

  return (
    <div className="me-selector">
      <div className="me-selector-header">
        <div>
          <h2 className="me-selector-title">YOUR EVENTS</h2>
          <p className="me-selector-sub">Choose an event or create a new one</p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {isRootAdmin && (
            <button className="me-admin-gear" onClick={() => navigate('/root-admin')} title="Root Admin">
              ⚙️
            </button>
          )}
          <button className="me-selector-create-btn" onClick={() => setShowCreate(!showCreate)}>
            {showCreate ? '✕ Cancel' : '+ CREATE EVENT'}
          </button>
          <AvatarDropdown
            user={user}
            onSignOut={async () => { await signOut(); navigate('/'); }}
            onProfile={() => navigate('/profile')}
          />
        </div>
      </div>

      {showCreate && (
        <form className="me-create-form" onSubmit={handleCreate}>
          <h3 className="me-create-form-title">New Event</h3>
          {error && <div className="me-create-error">{error}</div>}
          <div className="me-create-field"><label>Name</label><input type="text" placeholder="e.g. Euro 2026 Trip" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} autoFocus /></div>
          <div className="me-create-field"><label>Description</label><textarea placeholder="What's this event about?" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} /></div>
          <div className="me-create-field">
            <label>Type</label>
            <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
              <option value="trip">✈️ Trip</option>
              <option value="gathering">🎉 Gathering</option>
              <option value="league">🏆 League</option>
              <option value="custom">📋 Custom</option>
            </select>
          </div>
          <button type="submit" className="me-create-submit">Create Event</button>
        </form>
      )}

      {events.length === 0 && !showCreate ? (
        <div className="me-selector-empty">
          <div className="me-selector-empty-icon">🎯</div>
          <h3>No events yet</h3>
          <p>Create your first event to get started with your group!</p>
          <button className="me-selector-create-btn" onClick={() => setShowCreate(true)}>+ Create Event</button>
        </div>
      ) : (
        <div className="me-selector-grid">
          {events.map((event) => {
            const meta = TYPE_META[event.type] || TYPE_META.custom;
            const eventMembers = eventMeta[event.id]?.members || [];
            const eventActivities = eventMeta[event.id]?.activities || [];
            const amOwner = isOwnerOf(event);
            const isEditing = editingId === event.id;

            return (
              <div key={event.id} className="me-card" onClick={() => !isEditing && navigate(`/me/${event.id}`)}>
                <div className="me-card-top">
                  <span className="me-card-icon">{meta.icon}</span>
                  <span className="me-card-type-badge" style={{ color: meta.color, borderColor: meta.color + '44', background: meta.color + '15' }}>
                    {event.type}
                  </span>
                  {amOwner && (
                    <div className="me-card-actions">
                      <button className="me-card-action-btn" onClick={(e) => handleStartEdit(e, event.id, event.name)} title="Edit event name">✏️</button>
                      <button className="me-card-action-btn me-card-action-btn--danger" onClick={(e) => handleDelete(e, event.id, event.name)} title="Delete event">🗑️</button>
                    </div>
                  )}
                </div>

                {isEditing ? (
                  <div className="me-card-edit" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="me-card-edit-input"
                      autoFocus
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSaveEdit(e, event.id); if (e.key === 'Escape') handleCancelEdit(e); }}
                    />
                    <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                      <button className="me-card-edit-save" onClick={(e) => handleSaveEdit(e, event.id)}>Save</button>
                      <button className="me-card-edit-cancel" onClick={handleCancelEdit}>Cancel</button>
                    </div>
                  </div>
                ) : (
                  <h3 className="me-card-name">{event.name}</h3>
                )}

                {event.description && <p className="me-card-desc">{event.description}</p>}
                <div className="me-card-stats">
                  <span className="me-card-stat"><span className="me-card-stat-icon">👥</span>{eventMembers.length} member{eventMembers.length !== 1 ? 's' : ''}</span>
                  <span className="me-card-stat"><span className="me-card-stat-icon">📌</span>{eventActivities.length} activit{eventActivities.length !== 1 ? 'ies' : 'y'}</span>
                </div>
                <div className="me-card-footer">
                  <span className={`me-card-status me-card-status--${event.status}`}>{event.status}</span>
                  {amOwner && <span style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontWeight: 600 }}>OWNER</span>}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
